// ---------------------------------------------------------------------------
// Theme presets — color palette overrides merged into the base Chakra theme.
// Each preset redefines: brand, brandScheme, accent, navy, secondaryGray.300
// (light page bg), and brand-tinted shadows.  Component styles stay shared.
// ---------------------------------------------------------------------------

export type PresetId = 'default' | 'midnight' | 'ember' | 'forest' | 'clay';

export interface ThemePreset {
	id: PresetId;
	/** i18n key suffix — used as translate(`appearance.preset_${nameKey}`) */
	nameKey: string;
	/** Three representative hex colors shown in the picker UI */
	swatches: [string, string, string];
	colors: {
		brand: Record<string, string>;
		brandScheme: Record<string, string>;
		accent: Record<string, string>;
		/** Only override 700-900 (dark surfaces) */
		navy: Record<string, string>;
		/** Override 300 = light-mode page background */
		secondaryGray: Record<string, string>;
	};
	shadows: {
		brand: string;
		'brand-lg': string;
	};
}

// ---------------------------------------------------------------------------
// Default — current blue brand (exact values from styles.ts)
// ---------------------------------------------------------------------------
const defaultPreset: ThemePreset = {
	id: 'default',
	nameKey: 'default',
	swatches: ['#023F7F', '#3578BF', '#10B981'],
	colors: {
		brand: {
			50: '#EBF2FA', 100: '#C8DCF0', 200: '#A5C6E6', 300: '#6D9FD3',
			400: '#3578BF', 500: '#023F7F', 600: '#023569', 700: '#012B54',
			800: '#01213F', 900: '#01172A',
		},
		brandScheme: {
			50: '#EBF2FA', 100: '#C8DCF0', 200: '#A5C6E6', 300: '#6D9FD3',
			400: '#3578BF', 500: '#023F7F', 600: '#023569', 700: '#012B54',
			800: '#01213F', 900: '#01172A',
		},
		accent: {
			50: '#ECFDF5', 100: '#D1FAE5', 200: '#A7F3D0', 300: '#6EE7B7',
			400: '#34D399', 500: '#10B981', 600: '#059669', 700: '#047857',
			800: '#065F46', 900: '#064E3B',
		},
		navy: {
			50: '#d0dcfb', 100: '#aac0fe', 200: '#a3b9f8', 300: '#728fea',
			400: '#3652ba', 500: '#1b3bbb', 600: '#24388a',
			700: '#27272A', 800: '#18181B', 900: '#09090B',
		},
		secondaryGray: { 300: '#F4F5F7' },
	},
	shadows: {
		brand: '0px 4px 16px rgba(2, 63, 127, 0.20), 0px 2px 6px rgba(2, 63, 127, 0.10)',
		'brand-lg': '0px 8px 32px rgba(2, 63, 127, 0.25), 0px 4px 12px rgba(2, 63, 127, 0.12)',
	},
};

// ---------------------------------------------------------------------------
// Midnight — deep indigo / violet
// ---------------------------------------------------------------------------
const midnightPreset: ThemePreset = {
	id: 'midnight',
	nameKey: 'midnight',
	swatches: ['#4338CA', '#7C3AED', '#8B5CF6'],
	colors: {
		brand: {
			50: '#EEF2FF', 100: '#E0E7FF', 200: '#C7D2FE', 300: '#A5B4FC',
			400: '#818CF8', 500: '#4338CA', 600: '#3730A3', 700: '#312E81',
			800: '#23215E', 900: '#1E1B4B',
		},
		brandScheme: {
			50: '#EEF2FF', 100: '#E0E7FF', 200: '#C7D2FE', 300: '#A5B4FC',
			400: '#818CF8', 500: '#4338CA', 600: '#3730A3', 700: '#312E81',
			800: '#23215E', 900: '#1E1B4B',
		},
		accent: {
			50: '#F5F3FF', 100: '#EDE9FE', 200: '#DDD6FE', 300: '#C4B5FD',
			400: '#A78BFA', 500: '#8B5CF6', 600: '#7C3AED', 700: '#6D28D9',
			800: '#5B21B6', 900: '#4C1D95',
		},
		navy: {
			50: '#d0dcfb', 100: '#aac0fe', 200: '#a3b9f8', 300: '#728fea',
			400: '#3652ba', 500: '#1b3bbb', 600: '#24388a',
			700: '#1E1B3A', 800: '#13112A', 900: '#0F0D1A',
		},
		secondaryGray: { 300: '#F5F3FF' },
	},
	shadows: {
		brand: '0px 4px 16px rgba(67, 56, 202, 0.20), 0px 2px 6px rgba(67, 56, 202, 0.10)',
		'brand-lg': '0px 8px 32px rgba(67, 56, 202, 0.25), 0px 4px 12px rgba(67, 56, 202, 0.12)',
	},
};

// ---------------------------------------------------------------------------
// Ember — warm amber / earth tones
// ---------------------------------------------------------------------------
const emberPreset: ThemePreset = {
	id: 'ember',
	nameKey: 'ember',
	swatches: ['#B45309', '#D97706', '#F59E0B'],
	colors: {
		brand: {
			50: '#FFFBEB', 100: '#FEF3C7', 200: '#FDE68A', 300: '#FCD34D',
			400: '#FBBF24', 500: '#B45309', 600: '#92400E', 700: '#78350F',
			800: '#5C2D0E', 900: '#451A03',
		},
		brandScheme: {
			50: '#FFFBEB', 100: '#FEF3C7', 200: '#FDE68A', 300: '#FCD34D',
			400: '#FBBF24', 500: '#B45309', 600: '#92400E', 700: '#78350F',
			800: '#5C2D0E', 900: '#451A03',
		},
		accent: {
			50: '#FFFBEB', 100: '#FEF3C7', 200: '#FDE68A', 300: '#FCD34D',
			400: '#FBBF24', 500: '#F59E0B', 600: '#D97706', 700: '#B45309',
			800: '#92400E', 900: '#78350F',
		},
		navy: {
			50: '#fbd0c0', 100: '#f5c4ab', 200: '#e8b89f', 300: '#c49878',
			400: '#8a5a3a', 500: '#6b3f25', 600: '#4a2c1a',
			700: '#292524', 800: '#1C1917', 900: '#0C0A09',
		},
		secondaryGray: { 300: '#FFFBF5' },
	},
	shadows: {
		brand: '0px 4px 16px rgba(180, 83, 9, 0.20), 0px 2px 6px rgba(180, 83, 9, 0.10)',
		'brand-lg': '0px 8px 32px rgba(180, 83, 9, 0.25), 0px 4px 12px rgba(180, 83, 9, 0.12)',
	},
};

// ---------------------------------------------------------------------------
// Forest — natural greens
// ---------------------------------------------------------------------------
const forestPreset: ThemePreset = {
	id: 'forest',
	nameKey: 'forest',
	swatches: ['#166534', '#22C55E', '#4ADE80'],
	colors: {
		brand: {
			50: '#F0FDF4', 100: '#DCFCE7', 200: '#BBF7D0', 300: '#86EFAC',
			400: '#4ADE80', 500: '#166534', 600: '#15803D', 700: '#14532D',
			800: '#0D3B20', 900: '#052E16',
		},
		brandScheme: {
			50: '#F0FDF4', 100: '#DCFCE7', 200: '#BBF7D0', 300: '#86EFAC',
			400: '#4ADE80', 500: '#166534', 600: '#15803D', 700: '#14532D',
			800: '#0D3B20', 900: '#052E16',
		},
		accent: {
			50: '#F0FDF4', 100: '#DCFCE7', 200: '#BBF7D0', 300: '#86EFAC',
			400: '#4ADE80', 500: '#22C55E', 600: '#16A34A', 700: '#15803D',
			800: '#166534', 900: '#14532D',
		},
		navy: {
			50: '#c0fbd0', 100: '#abf5c4', 200: '#9fe8b8', 300: '#78c498',
			400: '#3a8a5a', 500: '#256b3f', 600: '#1a4a2c',
			700: '#1A2E1A', 800: '#0F1F0F', 900: '#052E16',
		},
		secondaryGray: { 300: '#F0FDF4' },
	},
	shadows: {
		brand: '0px 4px 16px rgba(22, 101, 52, 0.20), 0px 2px 6px rgba(22, 101, 52, 0.10)',
		'brand-lg': '0px 8px 32px rgba(22, 101, 52, 0.25), 0px 4px 12px rgba(22, 101, 52, 0.12)',
	},
};

// ---------------------------------------------------------------------------
// Clay — Mediterranean terracotta
// ---------------------------------------------------------------------------
const clayPreset: ThemePreset = {
	id: 'clay',
	nameKey: 'clay',
	swatches: ['#9C4221', '#C4713B', '#E8976A'],
	colors: {
		brand: {
			50: '#FDF2EB', 100: '#F5DBC8', 200: '#E8B99A', 300: '#D9956C',
			400: '#C4713B', 500: '#9C4221', 600: '#7C3418', 700: '#5C2712',
			800: '#451C0D', 900: '#2E1209',
		},
		brandScheme: {
			50: '#FDF2EB', 100: '#F5DBC8', 200: '#E8B99A', 300: '#D9956C',
			400: '#C4713B', 500: '#9C4221', 600: '#7C3418', 700: '#5C2712',
			800: '#451C0D', 900: '#2E1209',
		},
		accent: {
			50: '#FDF2EB', 100: '#F5DBC8', 200: '#E8B99A', 300: '#D9956C',
			400: '#E8976A', 500: '#C4713B', 600: '#9C4221', 700: '#7C3418',
			800: '#5C2712', 900: '#451C0D',
		},
		navy: {
			50: '#fbd0c0', 100: '#f5c4ab', 200: '#e8b89f', 300: '#c49878',
			400: '#8a5a3a', 500: '#6b3f25', 600: '#4a2c1a',
			700: '#2A2522', 800: '#1F1B19', 900: '#14110F',
		},
		secondaryGray: { 300: '#FDF2EB' },
	},
	shadows: {
		brand: '0px 4px 16px rgba(156, 66, 33, 0.20), 0px 2px 6px rgba(156, 66, 33, 0.10)',
		'brand-lg': '0px 8px 32px rgba(156, 66, 33, 0.25), 0px 4px 12px rgba(156, 66, 33, 0.12)',
	},
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const themePresets: Record<PresetId, ThemePreset> = {
	default: defaultPreset,
	midnight: midnightPreset,
	ember: emberPreset,
	forest: forestPreset,
	clay: clayPreset,
};

export const presetIds = Object.keys(themePresets) as PresetId[];
